/**
 * Search — search across all assigned playlists.
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePlaylistData } from "../lib/PlaylistDataContext";
import { isFavorited, toggleFavorite, isFavoritable } from "../lib/favorites";
import { filterForKids } from "../lib/profiles";
import { PLAYER } from "../lib/testIds";
import { getTileSize, gridClassesFor } from "../lib/tileSize";
import { Search as SearchIcon, ArrowLeft, Play, Tv, Star } from "lucide-react";

const TYPES = [
  { value: "", label: "All" },
  { value: "live_tv", label: "Live TV" },
  { value: "movie", label: "Movies" },
  { value: "series", label: "Series" },
  { value: "catchup", label: "Catch Up" },
];

export default function Search() {
  const navigate = useNavigate();
  const { channels, status } = usePlaylistData();
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [favTick, setFavTick] = useState(0);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (term.length < 2) return [];
    let list = channels.filter((c) =>
      (c.name || "").toLowerCase().includes(term) ||
      // v1.3.72 — also match by series_name so typing "Breaking" finds
      // the show even when the user has spelled their episode names
      // as `Breaking Bad S01E01 - Pilot`.
      (c.series_name || "").toLowerCase().includes(term),
    );
    if (type) list = list.filter((c) => c.content_type === type);
    list = filterForKids(list);

    // v1.3.72 — Series fix: when the result set contains series
    // episodes (either because the user filtered to Series or because
    // they did an "All" search and matched on series_name), collapse
    // the episodes down to ONE tile per show. Without this the user
    // searching "Breaking Bad" gets back 62 individual episode tiles
    // instead of a single show tile.
    const collapsed = [];
    const seriesSeen = new Map();
    for (const c of list) {
      const isSeriesEp = c.content_type === "series";
      if (!isSeriesEp) {
        collapsed.push(c);
        continue;
      }
      const showKey = (c.series_name || c.name || "").toLowerCase();
      if (!showKey) {
        collapsed.push(c);
        continue;
      }
      const existing = seriesSeen.get(showKey);
      if (existing) {
        existing.__episode_count += 1;
        // Prefer the episode with the earliest order so the tile's
        // stream_url at least starts the show from S01E01 if the
        // user taps Play directly on the search result.
        if ((c.order ?? Infinity) < (existing.order ?? Infinity)) {
          existing.stream_url = c.stream_url;
          existing.order = c.order;
        }
        // Pick up a logo if the first occurrence didn't carry one.
        if (!existing.logo_url && c.logo_url) existing.logo_url = c.logo_url;
        continue;
      }
      const tile = {
        ...c,
        // Show the SERIES name on the tile, not the episode name.
        name: c.series_name || c.name,
        // Stable id so React keys don't collide if the same show
        // appears in multiple categories (e.g. "Drama" + "Crime").
        id: `series://${showKey}`,
        // Flag we use below to route the click to the show drill-
        // down instead of straight to Play.
        __is_series_show: true,
        __episode_count: 1,
      };
      seriesSeen.set(showKey, tile);
      collapsed.push(tile);
    }
    return collapsed.slice(0, 200);
  }, [q, type, channels, favTick]);

  const loading = status === "loading";
  const touched = q.trim().length >= 2;

  const play = (item) => {
    // v1.3.72 — series tiles drill into the Browse page with the
    // series pre-selected via query params, rather than starting
    // episode 1 immediately. Movies / Live TV continue to play
    // straight from the result tile.
    if (item.__is_series_show) {
      // Route is `/player/browse/:type` — the :type segment must be in
      // the path, not the query string, or React Router falls through
      // to a 404. The Browse component reads `?series=` from
      // window.location.search to drive the auto-drill-down.
      navigate(
        `/player/browse/series?series=${encodeURIComponent(item.series_name || item.name)}`,
      );
      return;
    }
    navigate("/player/watch", {
      state: {
        stream_url: item.stream_url,
        name: item.name,
        logo: item.logo_url || "",
        type: item.content_type || "",
      },
    });
  };

  const onToggleFav = (e, item) => {
    e.stopPropagation();
    if (!isFavoritable(item.content_type)) return;
    toggleFavorite(item.content_type, item);
    setFavTick((t) => t + 1);
  };

  return (
    <div className="space-y-5">
      <button
        data-testid={PLAYER.btnBack}
        onClick={() => navigate("/player/menu")}
        className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Menu
      </button>

      <div className="bg-[#11141b] border border-white/5 rounded-2xl p-5">
        <div className="flex items-center gap-3 mb-4">
          <SearchIcon className="w-5 h-5 text-orange-400" />
          <h2 className="text-xl font-bold">Search</h2>
        </div>
        <input
          data-testid={PLAYER.searchInput}
          type="text"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            // v1.4.42 — ArrowDown from the search input jumps focus into
            // the first result tile so users on Smart-TV remotes can
            // reach the results (Chromium's spatial-nav often misfires
            // through the type-tabs sitting between input and grid).
            if (e.key === "ArrowDown") {
              const firstTile = document.querySelector('[data-testid^="search-tile-"] button');
              if (firstTile) {
                e.preventDefault();
                try { firstTile.focus({ preventScroll: false }); } catch { firstTile.focus(); }
                try { firstTile.scrollIntoView({ block: "nearest" }); } catch { /* */ }
              }
            }
          }}
          placeholder="Type to search across all playlists…"
          className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-base text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500/60"
        />
        <div className="flex flex-wrap gap-1.5 mt-3">
          {TYPES.map((t) => (
            <button
              key={t.value || "all"}
              onClick={() => setType(t.value)}
              className={`text-xs px-3 py-1 rounded-full transition-colors ${
                type === t.value
                  ? "bg-orange-500 text-white"
                  : "bg-white/5 text-slate-300 hover:bg-white/10"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-slate-400 text-sm">Loading playlist…</div>}

      {!loading && touched && results.length === 0 && (
        <div className="text-slate-500 text-sm">No matches.</div>
      )}

      {results.length > 0 && (
        <div className={`grid gap-4 ${gridClassesFor(getTileSize(), false)}`}>
          {results.map((item) => {
            const canFav = isFavoritable(item.content_type);
            const fav = canFav && isFavorited(item.content_type, item.stream_url);
            return (
              <div
                key={item.id}
                data-testid={`search-tile-${item.id}`}
                className="group relative bg-[#11141b] hover:bg-[#171b25] border border-white/5 hover:border-orange-500/40 rounded-xl overflow-hidden focus-within:border-orange-500 focus-within:z-10 focus-within-outline-inset"
              >
                <button
                  onClick={() => play(item)}
                  className="block w-full text-left active:scale-[0.98]"
                >
                  <div className="relative aspect-[2/3] bg-black overflow-hidden">
                    {item.logo_url ? (
                      <img
                        src={item.logo_url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Tv className="w-10 h-10 text-slate-700" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-3">
                      <Play className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="px-2.5 py-2">
                    <div className="text-sm font-medium truncate">{item.name}</div>
                    <div className="text-[10px] text-slate-500 capitalize">
                      {item.__is_series_show
                        ? `series · ${item.__episode_count} episode${item.__episode_count === 1 ? "" : "s"}`
                        : item.content_type?.replace("_", " ")}
                    </div>
                  </div>
                </button>
                {canFav && (
                  <button
                    data-testid={`fav-toggle-search-${item.id}`}
                    onClick={(e) => onToggleFav(e, item)}
                    aria-pressed={fav}
                    title={fav ? "Remove from favorites" : "Add to favorites"}
                    className={`absolute top-1.5 right-1.5 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                      fav
                        ? "bg-amber-400/95 text-black shadow-lg"
                        : "bg-black/55 text-white opacity-0 group-hover:opacity-100 hover:bg-amber-400/95 hover:text-black"
                    }`}
                  >
                    <Star className={`w-4 h-4 ${fav ? "fill-current" : ""}`} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
